/**
 * The earlier request for the chosen person and type of letter, fetched again whenever either
 * changes. Each answer is handed to `onLoaded` with the one handed over before it, so the form can
 * prefill it and let go of the previous person's answers; a late answer for a person or type that
 * has since changed is dropped. While it loads the page cannot send: the proxy would refuse a send
 * that does not name a request it is about to find.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchEarlierRequest } from '../../services/referenceLetterFormApi';
import type { EarlierRequest } from '../../types/referenceLetterForm';

type OnLoaded = (earlier: EarlierRequest | null, previous: EarlierRequest | null) => void;

export function useEarlierRequest(employeeId: string | null, type: string | null, onLoaded: OnLoaded) {
  const key = employeeId && type ? JSON.stringify({ employeeId, type }) : null;
  const [result, setResult] = useState<{ key: string; earlier: EarlierRequest | null; failed: boolean } | null>(null);
  const callback = useRef(onLoaded);
  const delivered = useRef<EarlierRequest | null>(null);
  useEffect(() => {
    callback.current = onLoaded;
  });

  useEffect(() => {
    if (!key) return undefined;
    let current = true;
    const settle = (earlier: EarlierRequest | null, failed: boolean) => {
      if (!current) return;
      setResult({ key, earlier, failed });
      callback.current(earlier, delivered.current);
      delivered.current = earlier;
    };
    fetchEarlierRequest(JSON.parse(key) as { employeeId: string; type: string })
      .then((earlier) => settle(earlier, false))
      .catch(() => settle(null, true));
    return () => {
      current = false;
    };
  }, [key]);

  const mine = result && result.key === key ? result : null;
  return {
    earlier: mine?.earlier ?? null,
    failed: Boolean(mine?.failed),
    pending: Boolean(key) && !mine,
    /**
     * A fresh form after a send: nothing on it came from an earlier request, and an answer from
     * before the send is stale (the send may just have filed the request it would now find).
     */
    forget: () => {
      delivered.current = null;
      setResult(null);
    },
  };
}
